import { type ReactElement, useState } from "react";
import {
  type Control,
  Controller,
  type FieldPath,
  type FieldValues,
  type UseFormGetValues,
} from "react-hook-form";
import {
  SizingWrapper,
  type SizingWrapperStyle,
} from "@/components/Wrapper/SizingWrapper";
import {
  Autocomplete,
  Box,
  Chip,
  InputAdornment,
  TextField,
  Tooltip,
} from "@mui/material";
import HelpIcon from "@mui/icons-material/Help";
import React from "react";
import { multilineSx } from "@/styles/sx/input";
import {
  getInputDelimiterJapanese,
  handleCleanArrayValue,
  handleSplitInputValues,
  INPUT_REGEX_SPLIT,
} from "@/utils/utils";

interface IProps<
  T extends FieldValues,
  TPath extends FieldPath<T> = FieldPath<T>
> {
  control: Control<T> | undefined;
  name: TPath;
  getValues: UseFormGetValues<T>; // 初期値を取得したいためgetValuesにしてます
  id?: string;
  label?: string;
  required?: boolean;
  autoFocus?: boolean;
  onKeyDown?: (event: React.KeyboardEvent<HTMLDivElement>) => void;
  handleTransformValue?: (value: string) => string;
  size?: SizingWrapperStyle;
  disabled?: boolean;
  readOnly?: boolean;
  tooltipText?: React.ReactNode;
  callbackOnChange?: (value: string[]) => void;
}

/**
 * @description
 *   - Autocompleteのマルチセレクト（フリーテキスト）コンポーネント
 *     - 参考：https://mui.com/material-ui/react-autocomplete/#multiple-values
 *   - 内部State
 *     - options
 *       - 入力候補を表示するための配列
 *       - 入力した値が重複した値を除いて全て入ります
 *       - 表示するタイミングでfilterOptionsを使用してフィルタリングしています
 *         - 選択されている項目は除外されます
 *   - 変換処理
 *     - 本コンポーネント内ではtrim()し、空白を除外して、重複した値を削除します
 *     - 上記以外の変換処理はhandleTransformValueを使用してください
 *     - onChange、onInputChange、onBlur、onPaste時に同一の上記変換処理が実行されます
 *   - 区切り文字で入力確定
 *     - 入力時またはコピー＆ペーストのタイミングで、区切り文字（カンマ、タブ、改行）によって分割し、入力値を更新します
 *   - エラー
 *     - 複数エラー表示に対応しています
 *
 * @param
 *   - control: react-hook-formのcontrol
 *   - name: react-hook-formのname
 *   - id: コンポーネントのID
 *   - label: ラベル名
 *   - required: 必須マークを表示するかどうか
 *   - autoFocus: 自動的にフォーカスを当てるかどうか
 *   - onKeyDown: キーが押されたときの処理
 *   - handleTransformValue: 入力値を変換するための関数（入力確定、onBlur時に呼ばれる）
 *   - size: サイズ設定
 *   - disabled: 入力を無効にするかどうか
 *   - readOnly: 読み取り専用かどうか
 *   - tooltipText: ツールチップのテキスト
 *
 */
export const CAutocompleteMultiFreeSoloTextField = <
  T extends FieldValues,
  TPath extends FieldPath<T>
>(
  props: IProps<T, TPath>
): ReactElement => {
  const {
    name,
    control,
    getValues,
    autoFocus,
    disabled,
    id,
    label,
    size,
    onKeyDown,
    handleTransformValue,
    required,
    readOnly,
    tooltipText = (
      <>
        区切り文字（{getInputDelimiterJapanese()}）で複数入力が可能です
        <br />
        コピー＆ペーストでも複数項目を一括で入力できます
      </>
    ),
    callbackOnChange,
  } = props;

  const [options, setOptions] = useState<string[]>(getValues(name));

  // コンポーネント内でIME入力中かどうかを追跡するstate
  const [isComposing, setIsComposing] = useState(false);
  // 最後に押されたキーを覚えておく
  const [lastKey, setLastKey] = useState<string | null>(null);

  return (
    <Controller
      name={name}
      control={control}
      render={({ field: { onBlur, onChange, ref, value }, fieldState }) => {
        const currentValues = (value ?? []) as string[];

        /**
         * 入力値と入力候補の更新処理
         * 並び順は入力された順番にする
         *
         * @param newCurrentValues 新規で入力された値を含む配列
         */
        const setCurrentAndOptions = (newCurrentValues: string[]): void => {
          if (currentValues.length >= newCurrentValues.length) {
            /**
             * 削除もしくは入力変更がない場合
             * optionsにはすでに含まれているので、optionsを更新する必要はない
             */
            if (callbackOnChange && newCurrentValues.length !== 0) {
              // 値が削除されたらコールバックを実行
              callbackOnChange(newCurrentValues);
            }
            onChange(newCurrentValues);
            return;
          }
          // 入力値の変換処理実行
          const transformedNewValues = handleTransformValue
            ? newCurrentValues.map((value) => handleTransformValue(value))
            : newCurrentValues;

          // クリーンにして入力値を更新
          const cleanedCurrentValues = handleCleanArrayValue([
            ...currentValues, // 入力された順番にするためcurrentValuesが先
            ...transformedNewValues,
          ]);
          if (callbackOnChange) {
            // 値が追加されたらコールバックを実行
            callbackOnChange(cleanedCurrentValues);
          }
          onChange(cleanedCurrentValues);

          // クリーンにして入力候補を更新
          setOptions(
            handleCleanArrayValue([
              ...options, // 入力された順番にするためoptionsが先
              ...cleanedCurrentValues,
            ])
          );
        };

        return (
          <SizingWrapper size={size}>
            <Autocomplete
              id={id}
              multiple
              freeSolo
              fullWidth
              disabled={disabled}
              clearOnBlur={true} // すでに選択済みの値を消す
              options={options}
              value={currentValues}
              readOnly={readOnly}
              filterOptions={
                /**
                 * 入力候補のフィルタリング処理
                 * - 表示前に実行される
                 */
                (options, params) => {
                  const input = params.inputValue.toLowerCase();

                  // 値が一致しないものだけ表示（選択済みは除外）
                  const filtered = options.filter(
                    (opt) =>
                      opt.toLowerCase().includes(input) &&
                      !currentValues.includes(opt)
                  );

                  // 入力値が新規なら候補として追加表示（freeSolo対策）
                  if (
                    input !== "" &&
                    !options.includes(input) &&
                    !currentValues.includes(input)
                  ) {
                    // 入力中に入力候補に存在しない値になった場合は入力候補を表示
                    filtered.push(input);
                  }

                  return filtered;
                }
              }
              onChange={
                /**
                 * 入力確定時の処理（Enter、バックスペース、全削除ボタン実行時）
                 * newValuesは入力値を含む値
                 */
                (_, newValues) => {
                  setCurrentAndOptions(newValues);
                }
              }
              onInputChange={
                /**
                 * 入力値が変更されたときの処理
                 * - reason
                 *   - "input"
                 *     - 1文字入力するごとに呼ばれる
                 *     - onKeyDown→onInputChangeの順番
                 *     - onKeyDownでpreventDefault()を実行すると呼ばれない
                 *   - "reset"
                 *     - 入力確定されたときに呼ばれる
                 *     - onInputChange→onChangeの順番
                 *   - "clear"
                 *     - 全削除ボタンを押したときのみ呼ばれる
                 *     - onInputChange→onChangeの順番
                 * @param newInputValue 新しい入力値、reasonがinput以外の場合は空文字列
                 * @param reason 変更の理由
                 */
                (_, newInputValue, reason) => {
                  if (
                    reason === "input" &&
                    INPUT_REGEX_SPLIT.test(newInputValue) &&
                    !isComposing // IME入力中でない場合のみ実行、IME入力中の場合はonKeyDownで実行
                  ) {
                    const splitValues = handleSplitInputValues(newInputValue);
                    setCurrentAndOptions([...currentValues, ...splitValues]);
                  }
                  if (reason === "clear") {
                    if (callbackOnChange) {
                      // 全削除ボタンを押したときは空配列を渡す
                      callbackOnChange([]);
                    }
                  }
                }
              }
              onKeyDown={(event) => {
                setLastKey(event.key);

                if (onKeyDown) {
                  onKeyDown(event);
                }
              }}
              onBlur={(e) => {
                const inputValue = (e.target as HTMLInputElement).value;

                setCurrentAndOptions(
                  [...currentValues, inputValue || []].flat() // 未入力の場合は空配列を追加するためflat()を使用
                );

                // バリデーション検知するために必要な処理
                onBlur();
              }}
              onPaste={(e) => {
                // 複数入力に対応するため、デフォルト動作を防止
                e.preventDefault();

                // クリップボードからテキストを取得
                const pastedText = e.clipboardData.getData("text");

                // 区切り文字で分割
                const inputValues = handleSplitInputValues(pastedText);

                if (inputValues.length === 0) {
                  // 分割後に有効な値がない場合は何もしない
                  return;
                }

                setCurrentAndOptions([...currentValues, ...inputValues]);
              }}
              onCompositionStart={() => {
                setIsComposing(true);
              }}
              onCompositionEnd={(e) => {
                setIsComposing(false);
                if (lastKey === "Process" || lastKey === "Enter") {
                  const inputValue = (e.target as HTMLInputElement).value;
                  if (INPUT_REGEX_SPLIT.test(inputValue)) {
                    const splitValues = handleSplitInputValues(inputValue);
                    setCurrentAndOptions([...currentValues, ...splitValues]);
                  }
                }
                setLastKey(null);
              }}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => {
                  const { key, ...rest } = getTagProps({ index });
                  return (
                    <Chip
                      key={option}
                      label={option}
                      sx={{
                        cursor: "pointer",
                        // 複数行表示にするためのスタイル
                        height: "auto",
                        "& .MuiChip-label": {
                          display: "block",
                          ...multilineSx,
                        },
                      }}
                      {...rest}
                    />
                  );
                })
              }
              renderOption={(props, option) => {
                const { key, ...rest } = props as {
                  key: string;
                } & React.HTMLAttributes<HTMLLIElement>;
                return (
                  <Box component="li" key={key} {...rest} sx={multilineSx}>
                    {option}
                  </Box>
                );
              }}
              slotProps={{
                // 一括削除ボタンの位置をtooltipの左側にする
                clearIndicator: {
                  sx: {
                    marginRight: "32px",
                  },
                },
              }}
              sx={{
                // 入力値が一括削除ボタンに被らないようにする
                "& .MuiOutlinedInput-root": {
                  paddingRight: "74px !important",
                },
              }}
              renderInput={(params) => {
                const { InputProps, ...rest } = params;
                const { endAdornment, ...restInputProps } = InputProps;
                return (
                  <TextField
                    {...rest}
                    label={label}
                    inputRef={ref}
                    required={required}
                    autoFocus={autoFocus}
                    error={Boolean(fieldState.error)}
                    helperText={
                      // 複数エラー表示対応
                      fieldState.error?.types
                        ? Object.values(fieldState.error.types)
                            .flat()
                            .map((msg, i, arr) => (
                              <React.Fragment key={i}>
                                {msg}
                                {i < arr.length - 1 && <br />}
                              </React.Fragment>
                            ))
                        : fieldState.error?.message
                    }
                    InputProps={{
                      ...restInputProps,
                      endAdornment: (
                        <>
                          {/* 入力できる場合のみ表示 */}
                          {!disabled && !readOnly && (
                            <>
                              {/* endAdornmentには一括削除ボタンが含まれます */}
                              {endAdornment}
                              <InputAdornment position="end">
                                <Tooltip
                                  title={tooltipText}
                                  placement="bottom"
                                  sx={{
                                    position: "absolute",
                                    top: "50%",
                                    transform: "translate(0, -50%)",
                                    right: "14px", // Autocompleteを使用するとずれるので、他のTextFieldと同じ値にする
                                  }}
                                >
                                  <HelpIcon />
                                </Tooltip>
                              </InputAdornment>
                            </>
                          )}
                        </>
                      ),
                    }}
                  />
                );
              }}
            />
          </SizingWrapper>
        );
      }}
    />
  );
};
